import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Link,
  Modal,
  ProgressBar,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  Tabs,
  Textarea,
  Toggle,
  type SelectProps,
  type TableProps,
} from '@cloudscape-design/components';
import {
  fetchDemos,
  createDemo,
  deployDemo,
  anonymizeDemo,
  extendDemo,
  teardownDemo,
  healthCheckDemo,
  ingestArtifact,
  type DemoInstance,
  type DemoType,
  type DataClassification,
  type DemoHostingType,
} from '../api/demo-catalog';
import { fetchTenants } from '../api/tenants';
import type { Tenant } from '../types/tenant';

const STATUS_MAP: Record<string, { type: 'pending' | 'in-progress' | 'success' | 'error' | 'warning' | 'stopped'; label: string }> = {
  provisioning: { type: 'pending', label: 'Provisioning' },
  deploying: { type: 'in-progress', label: 'Deploying' },
  live: { type: 'success', label: 'Live' },
  anonymizing: { type: 'in-progress', label: 'Anonymizing' },
  expiring: { type: 'warning', label: 'Expiring' },
  expired: { type: 'error', label: 'Expired' },
  extended: { type: 'success', label: 'Extended' },
  teardown: { type: 'stopped', label: 'Tearing Down' },
  archived: { type: 'stopped', label: 'Archived' },
  failed: { type: 'error', label: 'Failed' },
};

const DATA_CLASS_BADGE: Record<string, { color: 'red' | 'blue' | 'grey' | 'green'; label: string }> = {
  customer_data: { color: 'red', label: 'Customer Data' },
  anonymized: { color: 'blue', label: 'Anonymized' },
  synthetic: { color: 'grey', label: 'Synthetic' },
  public: { color: 'green', label: 'Public' },
};

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatHours(h?: number): string {
  if (h === undefined || h === null) return '—';
  if (h <= 0) return 'Expired';
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

export function DemoCatalog() {
  const [demos, setDemos] = useState<DemoInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [formSlug, setFormSlug] = useState('');
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formProduct, setFormProduct] = useState('');
  const [formType, setFormType] = useState<SelectProps.Option>({ label: 'Customer Demo', value: 'customer' });
  const [formClassification, setFormClassification] = useState<SelectProps.Option>({ label: 'Customer Data', value: 'customer_data' });
  const [formHosting, setFormHosting] = useState<SelectProps.Option>({ label: 'Static (Frontend Only)', value: 'static' });
  const [formSource, setFormSource] = useState('');
  const [formTtl, setFormTtl] = useState('30');
  const [formTenant, setFormTenant] = useState<SelectProps.Option | null>(null);
  const [formCustomer, setFormCustomer] = useState('');
  const [formApiUrl, setFormApiUrl] = useState('');
  const [formTags, setFormTags] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Action modals
  const [actionDemo, setActionDemo] = useState<DemoInstance | null>(null);
  const [showAnonymize, setShowAnonymize] = useState(false);
  const [anonReplacements, setAnonReplacements] = useState('');
  const [anonBackendUrl, setAnonBackendUrl] = useState('');
  const [anonNewName, setAnonNewName] = useState('');
  const [anonNotes, setAnonNotes] = useState('');

  const [showDeploy, setShowDeploy] = useState(false);
  const [deploySource, setDeploySource] = useState('');
  const [deployVersion, setDeployVersion] = useState('');

  const [showExtend, setShowExtend] = useState(false);
  const [extendDays, setExtendDays] = useState('7');
  const [extendReason, setExtendReason] = useState('');

  const [healthResults, setHealthResults] = useState<Record<string, { code: number; latencyMs: number; error?: string }>>({});

  // Preview state
  const [previewDemo, setPreviewDemo] = useState<DemoInstance | null>(null);
  const [previewMode, setPreviewMode] = useState<'mobile' | 'desktop'>('mobile');

  // Publish artifact state
  const [publishSlug, setPublishSlug] = useState('');
  const [publishName, setPublishName] = useState('');
  const [publishCustomer, setPublishCustomer] = useState('');
  const [publishProduct, setPublishProduct] = useState('');
  const [publishArtifact, setPublishArtifact] = useState('');
  const [publishType, setPublishType] = useState<SelectProps.Option>({ label: 'JSX (React Component)', value: 'jsx' });
  const [publishDesc, setPublishDesc] = useState('');
  const [publishTags, setPublishTags] = useState('');
  const [publishTtl, setPublishTtl] = useState('30');
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<{ url: string; demoId: string; slug: string } | null>(null);

  useEffect(() => { loadDemos(); }, []);

  async function loadDemos() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDemos();
      setDemos(data.demos);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function openCreate() {
    setShowCreate(true);
    try { const res = await fetchTenants(); setTenants(res.tenants); } catch { /* ok */ }
  }

  async function handleCreate() {
    if (!formSlug || !formName || !formProduct || !formSource) return;
    setSubmitting(true);
    try {
      await createDemo({
        slug: formSlug, name: formName, description: formDesc,
        productId: formProduct,
        demoType: (formType.value || 'customer') as DemoType,
        dataClassification: (formClassification.value || 'customer_data') as DataClassification,
        hostingType: (formHosting.value || 'static') as DemoHostingType,
        sourceTemplate: formSource,
        ttlDays: parseInt(formTtl) || 30,
        tenantId: formTenant?.value,
        customerName: formCustomer || undefined,
        apiProxy: formApiUrl ? { apiUrl: formApiUrl } : undefined,
        tags: formTags ? formTags.split(',').map((t) => t.trim()) : [],
      });
      setShowCreate(false);
      loadDemos();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeploy() {
    if (!actionDemo) return;
    setSubmitting(true);
    try {
      await deployDemo(actionDemo.demoId, { source: deploySource || undefined, version: deployVersion || undefined });
      setShowDeploy(false);
      loadDemos();
    } catch (err) { setError(err instanceof Error ? err.message : 'Deploy failed'); }
    finally { setSubmitting(false); }
  }

  async function handleAnonymize() {
    if (!actionDemo) return;
    setSubmitting(true);
    try {
      let replacements: Record<string, string> = {};
      if (anonReplacements.trim()) {
        replacements = JSON.parse(anonReplacements);
      }
      await anonymizeDemo(actionDemo.demoId, {
        envVarReplacements: replacements,
        swapBackendUrl: anonBackendUrl || undefined,
        anonymizedBy: 'operator',
        newName: anonNewName || undefined,
        notes: anonNotes || undefined,
      });
      setShowAnonymize(false);
      loadDemos();
    } catch (err) { setError(err instanceof Error ? err.message : 'Anonymize failed'); }
    finally { setSubmitting(false); }
  }

  async function handleExtend() {
    if (!actionDemo) return;
    setSubmitting(true);
    try {
      await extendDemo(actionDemo.demoId, { additionalDays: parseInt(extendDays) || 7, reason: extendReason });
      setShowExtend(false);
      loadDemos();
    } catch (err) { setError(err instanceof Error ? err.message : 'Extend failed'); }
    finally { setSubmitting(false); }
  }

  async function handleTeardown(demo: DemoInstance) {
    if (!confirm(`Tear down "${demo.name}"? This deletes all S3 content and archives the demo.`)) return;
    try {
      await teardownDemo(demo.demoId);
      loadDemos();
    } catch (err) { setError(err instanceof Error ? err.message : 'Teardown failed'); }
  }

  async function handleHealth(demo: DemoInstance) {
    try {
      const result = await healthCheckDemo(demo.demoId);
      setHealthResults((prev) => ({ ...prev, [demo.demoId]: result }));
    } catch (err) { setError(err instanceof Error ? err.message : 'Health check failed'); }
  }

  async function handlePublishArtifact() {
    if (!publishSlug || !publishName || !publishCustomer || !publishProduct || !publishArtifact) return;
    setPublishing(true);
    setPublishResult(null);
    try {
      const result = await ingestArtifact({
        slug: publishSlug,
        name: publishName,
        customerName: publishCustomer,
        productId: publishProduct,
        artifact: publishArtifact,
        artifactType: (publishType.value || 'jsx') as 'jsx' | 'html' | 'vite-zip',
        description: publishDesc || undefined,
        tags: publishTags ? publishTags.split(',').map((t) => t.trim()) : [],
        ttlDays: parseInt(publishTtl) || 30,
      });
      setPublishResult({ url: result.url, demoId: result.demoId, slug: result.slug });
      loadDemos();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed');
    } finally {
      setPublishing(false);
    }
  }

  // Filter by tab
  const filtered = activeTab === 'all' ? demos
    : activeTab === 'customer' ? demos.filter((d) => d.demoType === 'customer')
    : activeTab === 'showcase' ? demos.filter((d) => d.demoType === 'showcase')
    : activeTab === 'expiring' ? demos.filter((d) => d.isExpiring || d.isExpired)
    : demos;

  const columns: TableProps.ColumnDefinition<DemoInstance>[] = [
    { id: 'name', header: 'Name', width: 180, cell: (d) => d.name },
    { id: 'type', header: 'Type', width: 100, cell: (d) => <Badge color={d.demoType === 'customer' ? 'blue' : 'green'}>{d.demoType}</Badge> },
    { id: 'classification', header: 'Data', width: 130, cell: (d) => {
      const dc = DATA_CLASS_BADGE[d.dataClassification];
      return dc ? <Badge color={dc.color}>{dc.label}</Badge> : d.dataClassification;
    }},
    { id: 'status', header: 'Status', width: 120, cell: (d) => {
      const s = STATUS_MAP[d.status] || STATUS_MAP.failed;
      return <StatusIndicator type={s.type}>{s.label}</StatusIndicator>;
    }},
    { id: 'product', header: 'Product', width: 100, cell: (d) => d.productId },
    { id: 'customer', header: 'Customer', width: 130, cell: (d) => d.customerName || '—' },
    { id: 'ttl', header: 'TTL', width: 80, cell: (d) => formatHours(d.hoursRemaining) },
    { id: 'version', header: 'Version', width: 100, cell: (d) => d.currentVersion || '—' },
    { id: 'url', header: 'URL', width: 180, cell: (d) => d.status === 'live' ? <Link external href={d.url}>{d.slug}</Link> : '—' },
    { id: 'health', header: 'Health', width: 100, cell: (d) => {
      const h = healthResults[d.demoId];
      if (!h) return <Button variant="inline-link" onClick={() => handleHealth(d)}>Check</Button>;
      return h.code >= 200 && h.code < 400
        ? <StatusIndicator type="success">{h.latencyMs}ms</StatusIndicator>
        : <StatusIndicator type="error">{h.error || h.code}</StatusIndicator>;
    }},
    { id: 'actions', header: '', width: 320, cell: (d) => (
      <SpaceBetween direction="horizontal" size="xxs">
        {d.status === 'live' && <Button variant="inline-link" onClick={() => setPreviewDemo(d)}>Preview</Button>}
        {d.status === 'provisioning' && <Button onClick={() => { setActionDemo(d); setShowDeploy(true); }}>Deploy</Button>}
        {d.status === 'live' && <Button onClick={() => { setActionDemo(d); setShowDeploy(true); }}>Redeploy</Button>}
        {d.status === 'live' && d.demoType === 'customer' && <Button onClick={() => { setActionDemo(d); setShowAnonymize(true); }}>Anonymize</Button>}
        {(d.isExpiring || d.isExpired) && <Button onClick={() => { setActionDemo(d); setShowExtend(true); }}>Extend</Button>}
        {d.status !== 'archived' && d.status !== 'teardown' && <Button onClick={() => handleTeardown(d)}>Teardown</Button>}
      </SpaceBetween>
    )},
  ];

  const customerCount = demos.filter((d) => d.demoType === 'customer').length;
  const showcaseCount = demos.filter((d) => d.demoType === 'showcase').length;
  const expiringCount = demos.filter((d) => d.isExpiring || d.isExpired).length;

  return (
    <SpaceBetween size="l">
      <Header variant="h1"
        description="Host, manage, and catalog product demos. Customer demos with real data → anonymize → reusable showcase catalog."
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="refresh" onClick={loadDemos}>Refresh</Button>
            <Button variant="primary" onClick={openCreate}>Create Demo</Button>
          </SpaceBetween>
        }
      >Demo Catalog</Header>

      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}

      <Tabs activeTabId={activeTab} onChange={({ detail }) => setActiveTab(detail.activeTabId)} tabs={[
        { id: 'all', label: `All (${demos.length})` },
        { id: 'customer', label: `Customer (${customerCount})` },
        { id: 'showcase', label: `Showcase (${showcaseCount})` },
        { id: 'expiring', label: expiringCount > 0 ? `⚠️ Expiring (${expiringCount})` : `Expiring (0)` },
        { id: 'publish', label: '🚀 Publish Artifact' },
      ]} />

      {activeTab === 'publish' ? (
        <Container header={<Header variant="h2" description="Paste a JSX artifact from Claude, fill in the details, and publish it as a live demo in one click.">Publish Artifact</Header>}>
          <SpaceBetween size="m">
            {publishResult && (
              <Alert type="success" dismissible onDismiss={() => setPublishResult(null)}>
                Demo published! Live at: <Link external href={publishResult.url}>{publishResult.url}</Link>
                {' '}(ID: {publishResult.demoId})
              </Alert>
            )}
            <ColumnLayout columns={2}>
              <FormField label="Demo Slug" description="URL-safe identifier" constraintText="Lowercase, hyphens, no spaces">
                <Input value={publishSlug} onChange={({ detail }) => setPublishSlug(detail.value)} placeholder="acme-wafr-dashboard" />
              </FormField>
              <FormField label="Demo Name">
                <Input value={publishName} onChange={({ detail }) => setPublishName(detail.value)} placeholder="Acme Corp WAFR Dashboard" />
              </FormField>
            </ColumnLayout>
            <ColumnLayout columns={3}>
              <FormField label="Customer Name">
                <Input value={publishCustomer} onChange={({ detail }) => setPublishCustomer(detail.value)} placeholder="Acme Corp" />
              </FormField>
              <FormField label="Product ID">
                <Input value={publishProduct} onChange={({ detail }) => setPublishProduct(detail.value)} placeholder="one-ct-wafr" />
              </FormField>
              <FormField label="Artifact Type">
                <Select selectedOption={publishType} onChange={({ detail }) => setPublishType(detail.selectedOption)}
                  options={[
                    { label: 'JSX (React Component)', value: 'jsx', description: 'Paste raw JSX — auto-wrapped with React CDN' },
                    { label: 'HTML (Complete Page)', value: 'html', description: 'Paste full HTML — GA4 injected into <head>' },
                  ]} />
              </FormField>
            </ColumnLayout>
            <FormField label="Artifact Code" description="Paste the JSX component or HTML from Claude here">
              <Textarea value={publishArtifact} onChange={({ detail }) => setPublishArtifact(detail.value)}
                rows={20} placeholder={'// Paste your JSX artifact here\n// e.g. function App() { return <div>...</div> }'} />
            </FormField>
            <ColumnLayout columns={3}>
              <FormField label="Description">
                <Input value={publishDesc} onChange={({ detail }) => setPublishDesc(detail.value)} placeholder="WAFR compliance dashboard for Acme" />
              </FormField>
              <FormField label="Tags (comma-separated)">
                <Input value={publishTags} onChange={({ detail }) => setPublishTags(detail.value)} placeholder="sales, wafr, enterprise" />
              </FormField>
              <FormField label="TTL (days)">
                <Input value={publishTtl} onChange={({ detail }) => setPublishTtl(detail.value)} type="number" />
              </FormField>
            </ColumnLayout>
            <Box float="right">
              <Button variant="primary" onClick={handlePublishArtifact} loading={publishing}
                disabled={!publishSlug || !publishName || !publishCustomer || !publishProduct || !publishArtifact}>
                🚀 Publish Demo
              </Button>
            </Box>
          </SpaceBetween>
        </Container>
      ) : (
      <Table columnDefinitions={columns} items={filtered} loading={loading} loadingText="Loading demos..."
        trackBy="demoId" variant="full-page" stickyHeader stripedRows
        empty={<Box textAlign="center" padding="l"><Button variant="primary" onClick={openCreate}>Create Demo</Button></Box>}
        header={<Header counter={`(${filtered.length})`}>Demos</Header>}
      />
      )}

      {/* Create Modal */}
      <Modal visible={showCreate} onDismiss={() => setShowCreate(false)} header="Create Demo" size="large"
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate} loading={submitting} disabled={!formSlug || !formName || !formProduct || !formSource}>Create</Button>
        </SpaceBetween></Box>}>
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label="Slug"><Input value={formSlug} onChange={({ detail }) => setFormSlug(detail.value)} placeholder="acme-pc3-demo" /></FormField>
            <FormField label="Name"><Input value={formName} onChange={({ detail }) => setFormName(detail.value)} placeholder="Acme Corp PC3 Demo" /></FormField>
          </ColumnLayout>
          <FormField label="Description"><Textarea value={formDesc} onChange={({ detail }) => setFormDesc(detail.value)} rows={2} /></FormField>
          <ColumnLayout columns={3}>
            <FormField label="Product ID"><Input value={formProduct} onChange={({ detail }) => setFormProduct(detail.value)} placeholder="aeos-pc3" /></FormField>
            <FormField label="Demo Type"><Select selectedOption={formType} onChange={({ detail }) => setFormType(detail.selectedOption)}
              options={[{ label: 'Customer Demo', value: 'customer' }, { label: 'Showcase', value: 'showcase' }]} /></FormField>
            <FormField label="Data Classification"><Select selectedOption={formClassification} onChange={({ detail }) => setFormClassification(detail.selectedOption)}
              options={[
                { label: '🔴 Customer Data', value: 'customer_data' }, { label: '🔵 Anonymized', value: 'anonymized' },
                { label: '⚪ Synthetic', value: 'synthetic' }, { label: '🟢 Public', value: 'public' },
              ]} /></FormField>
          </ColumnLayout>
          <ColumnLayout columns={2}>
            <FormField label="Hosting Type"><Select selectedOption={formHosting} onChange={({ detail }) => setFormHosting(detail.selectedOption)}
              options={[{ label: 'Static (Frontend Only)', value: 'static' }, { label: 'Full Stack (Frontend + API)', value: 'fullstack' }]} /></FormField>
            <FormField label="Source Template (S3 key)"><Input value={formSource} onChange={({ detail }) => setFormSource(detail.value)} placeholder="demos/pc3/v1.0.0.zip" /></FormField>
          </ColumnLayout>
          {formHosting.value === 'fullstack' && (
            <FormField label="Backend API URL"><Input value={formApiUrl} onChange={({ detail }) => setFormApiUrl(detail.value)} placeholder="https://api.customer.example.com/v1" /></FormField>
          )}
          <ColumnLayout columns={3}>
            <FormField label="TTL (days)"><Input value={formTtl} onChange={({ detail }) => setFormTtl(detail.value)} type="number" /></FormField>
            <FormField label="Customer (for customer demos)">
              <Select selectedOption={formTenant} onChange={({ detail }) => { setFormTenant(detail.selectedOption); setFormCustomer(detail.selectedOption.description || ''); }}
                options={tenants.map((t) => ({ label: `${t.name} — ${t.companyName}`, value: t.tenantId, description: t.companyName }))}
                placeholder="Select tenant..." filteringType="auto" />
            </FormField>
            <FormField label="Tags (comma-separated)"><Input value={formTags} onChange={({ detail }) => setFormTags(detail.value)} placeholder="sales, pc3, enterprise" /></FormField>
          </ColumnLayout>
        </SpaceBetween>
      </Modal>

      {/* Deploy Modal */}
      <Modal visible={showDeploy} onDismiss={() => setShowDeploy(false)} header={`Deploy: ${actionDemo?.name || ''}`}
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setShowDeploy(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleDeploy} loading={submitting}>Deploy</Button>
        </SpaceBetween></Box>}>
        <SpaceBetween size="m">
          <FormField label="Source (S3 key)"><Input value={deploySource} onChange={({ detail }) => setDeploySource(detail.value)} placeholder="demos/pc3/v1.0.0.zip" /></FormField>
          <FormField label="Version"><Input value={deployVersion} onChange={({ detail }) => setDeployVersion(detail.value)} placeholder="v1.0.0" /></FormField>
        </SpaceBetween>
      </Modal>

      {/* Anonymize Modal */}
      <Modal visible={showAnonymize} onDismiss={() => setShowAnonymize(false)} header={`Anonymize: ${actionDemo?.name || ''}`} size="large"
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setShowAnonymize(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAnonymize} loading={submitting}>Anonymize</Button>
        </SpaceBetween></Box>}>
        <SpaceBetween size="m">
          <Alert type="warning">This will convert this customer demo into a reusable showcase. Customer data in env vars will be replaced. You'll need to re-deploy after anonymizing.</Alert>
          <FormField label="New Name (optional)"><Input value={anonNewName} onChange={({ detail }) => setAnonNewName(detail.value)} placeholder="PC3 Showcase Demo" /></FormField>
          <FormField label="Env Var Replacements (JSON)" description='{"__VITE_COMPANY_NAME__": "Acme Corp Demo"}'><Textarea value={anonReplacements} onChange={({ detail }) => setAnonReplacements(detail.value)} rows={4} /></FormField>
          <FormField label="Swap Backend URL (optional)" description="Replace the API proxy with a mock/seed data API"><Input value={anonBackendUrl} onChange={({ detail }) => setAnonBackendUrl(detail.value)} placeholder="https://demo-api.gdna.io/v1" /></FormField>
          <FormField label="Notes"><Textarea value={anonNotes} onChange={({ detail }) => setAnonNotes(detail.value)} rows={2} placeholder="What was anonymized..." /></FormField>
        </SpaceBetween>
      </Modal>

      {/* Extend Modal */}
      <Modal visible={showExtend} onDismiss={() => setShowExtend(false)} header={`Extend: ${actionDemo?.name || ''}`}
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setShowExtend(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleExtend} loading={submitting}>Extend</Button>
        </SpaceBetween></Box>}>
        <SpaceBetween size="m">
          <FormField label="Additional Days"><Input value={extendDays} onChange={({ detail }) => setExtendDays(detail.value)} type="number" /></FormField>
          <FormField label="Reason"><Input value={extendReason} onChange={({ detail }) => setExtendReason(detail.value)} placeholder="Customer needs more time to review" /></FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
